import React, { Dispatch } from 'react';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import { RootAction } from 'store/state';
import { setAuthModal } from 'store/state/app/actions';
import { InvestorsData } from 'utils/entities';
import { AuthenticationModalType } from 'components/authentication/types';
import { formatInvestorsYield } from 'utils/investors';
import {
  InvestorsHintBox,
  InvestorsInfoLabel,
  InvestorsInfoRoot,
  InvestorsInfoRow,
} from './styled';
import InfoIcon from 'assets/svg/info.svg';
import { InvestorNeighbourhoodCtaAnchor } from './investor-neighbourhood-cta';
import { DataDisplayValue } from './DataDisplayValue';
import { PreviousPrice } from '../styled';
import EstimatedPrice from '../estimated-price';

interface InvestorInfoProps {
  listingId: string;
  neighbourhoodDocId: string;
  investorsData: InvestorsData;
  priceDropped: number;
  showEstimatedPrice: boolean;
  scrollToPriceEstimation: () => void;
  openYieldTagModal: (id: string) => void;
  lastRowMarginTop?: number;
  isVisitor: boolean;
  openAuthModal: () => void;
}


const InvestorInfo: React.FC<InvestorInfoProps> = React.memo(props => {
  const {
    listingId,
    neighbourhoodDocId,
    investorsData,
    priceDropped,
    showEstimatedPrice,
    scrollToPriceEstimation,
    openYieldTagModal,
    lastRowMarginTop,
    isVisitor,
    openAuthModal,
  } = props;
  const { t, formatMoney } = useLocale();

  const approximateRent = investorsData && investorsData.approximateRent ? formatMoney(+investorsData.approximateRent) : null;
  const estimatedYield = investorsData && investorsData.yield ? formatInvestorsYield(investorsData.yield) : null;

  const onHintBoxClick = () => {
    if (openYieldTagModal) {
      openYieldTagModal(listingId);
    }
  };

  const onShowDataClick = () => {
    if (openAuthModal) {
      openAuthModal();
    }
  };

  if (!neighbourhoodDocId) {
    return null;
  }

  return (
    <InvestorsInfoRoot>
      <InvestorsInfoRow>
        {showEstimatedPrice || priceDropped ? (
          <InvestorsInfoLabel>{t('widget.prices.estimation.title')}</InvestorsInfoLabel>
        ) : null}
        {showEstimatedPrice ? (
          <EstimatedPrice handleScroll={scrollToPriceEstimation} withoutLabel />
        ) : (
          priceDropped ? <PreviousPrice data-auto="price">{formatMoney(priceDropped)}</PreviousPrice> : null
        )}
      </InvestorsInfoRow>
      <InvestorsInfoRow>
        <InvestorsInfoLabel>{t('widget.prices.yield.rent')}</InvestorsInfoLabel>
        <DataDisplayValue
          value={approximateRent}
          isVisitor={isVisitor}
          onShowDataClick={onShowDataClick}
        />
      </InvestorsInfoRow>
      <InvestorsInfoRow>
        <InvestorsHintBox onClick={onHintBoxClick}>
          <InvestorsInfoLabel>
            {t('widget.prices.yield.assessment')}
          </InvestorsInfoLabel>
          <InfoIcon />
        </InvestorsHintBox>
        <DataDisplayValue
          value={estimatedYield ? t('widget.unitpage.yield', { yield: estimatedYield }) : null}
          isVisitor={isVisitor}
          onShowDataClick={onShowDataClick}
        />
      </InvestorsInfoRow>
      <InvestorsInfoRow marginTop={lastRowMarginTop}>
        <InvestorNeighbourhoodCtaAnchor neighbourhoodDocId={neighbourhoodDocId} />
      </InvestorsInfoRow>
    </InvestorsInfoRoot>
  );
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  openAuthModal: () => dispatch(setAuthModal({ type: AuthenticationModalType.SignUp }, { isUserInitiated: true })),
});

export default connect(null, mapDispatchToProps)(InvestorInfo);
